import * as _ from "lodash";
import * as Redux from "redux";

import * as FetchActions from "./FetchActions";

import { ThunkResult, Utilities } from "../../shared/utilities";

import { HashMap } from "../../shared/model/HashMap";
import { getAgentGroup, State } from "../model/State";

import { AgentGroup, AgentVersionInfo } from "../../shared/model/AgentGroup";
import { Process } from "../model/Process";
import { ProcessVersionDetails } from "../model/ProcessVersion";
import { FetchId } from "./FetchActions";
import { IProcessVariable } from "../../processEditor/model/IProcessData";

export const OPEN_DEPLOY_MODAL = "OPEN_DEPLOY_MODAL";
export const CLOSE_DEPLOY_MODAL = "CLOSE_DEPLOY_MODAL";
export const SET_TARGET_AGENT_GROUP = "SET_TARGET_AGENT_GROUP";
export const DEPLOY = "DEPLOY";
export const SET_VERSION_TO_DEPLOY_ACTION = "SET_VERSION_TO_DEPLOY_ACTION";
export const UPDATE_VERSION_LIST_SHOWING_ALL_VERSIONS = "UPDATE_VERSION_LIST_SHOWING_ALL_VERSIONS";
export const OPEN_TAG_MODAL = "OPEN_TAG_MODAL";

export const DEFAULT_AGENT_GROUP_NAME = "Default";

export interface OpenDeployModalAction extends Redux.Action {
    processes: Process[];
    processVersions: HashMap<ProcessVersionDetails[]>;
    agentVersions: AgentVersionInfo[];
    defaultTargetAgentGroupName: string;
    defaultTargetEnvironmentName: string;
    availableAgentGroups: AgentGroup[];
    isSwitchVersion: boolean;
    suggestedProcessVariablesForDeployment: HashMap<IProcessVariable[]>;
}

export interface SetTargetAgentGroupAction extends Redux.Action {
    agentGroup: string;
    environment: string;
    suggestedProcessVariablesForDeployment: HashMap<IProcessVariable[]>;
}

export function setTargetAgentGroupAction(environment: string, agentGroup: string, suggestedProcessVariablesForDeployment: HashMap<IProcessVariable[]>): SetTargetAgentGroupAction {
    return { type: SET_TARGET_AGENT_GROUP, environment: environment, agentGroup: agentGroup, suggestedProcessVariablesForDeployment: suggestedProcessVariablesForDeployment };
}

export function setTargetAgentGroup(environment: string, agentGroup: string): ThunkResult<void, State> {
    return async (dispatch, getState) => {

        const processIds = Object.keys(getState().deployDialogState.suggestedProcessVariablesForDeployment).map(k => parseInt(k));

        const suggestedProcessVariablesForDeployment = await FetchActions.fetchSuggestedProcessVariablesForDeployment(agentGroup, processIds);

        try {
            return dispatch(setTargetAgentGroupAction(environment, agentGroup, suggestedProcessVariablesForDeployment));
        } catch (error) {
            return dispatch(FetchActions.reportFetchError(error));
        }
    };
}

export function closeDeployDialog(): Redux.Action {
    return { type: CLOSE_DEPLOY_MODAL };
}

export interface SetVersionToDeployAction extends Redux.Action {
    processGuid: string;
    version: number;
    processId: number;
    suggestedProcessVariablesForDeployment: IProcessVariable[];
}

export function updateVersionListShowingAllVersions(): Redux.Action {
    return { type: UPDATE_VERSION_LIST_SHOWING_ALL_VERSIONS };
}

export function setVersionToDeployAction(processGuid: string, version: number, processId: number, suggestedProcessVariablesForDeployment: IProcessVariable[]): SetVersionToDeployAction {
    return {
        type: SET_VERSION_TO_DEPLOY_ACTION,
        processGuid: processGuid,
        version: version,
        processId: processId,
        suggestedProcessVariablesForDeployment: suggestedProcessVariablesForDeployment,
    };
}

export function setVersionToDeploy(processGuid: string, version: number): ThunkResult<void, State> {
    return async (dispatch, getState) => {
        const state = getState();

        const processId = state.deployDialogState.processVersions[processGuid].find(v => v.version === version)!.processId;

        const suggestedProcessVariablesForDeployment = await FetchActions.fetchSuggestedProcessVariablesForDeployment(state.currentAgentGroupName, [processId]);

        try {
            return dispatch(setVersionToDeployAction(processGuid, version, processId, suggestedProcessVariablesForDeployment[processId]));
        } catch (error) {
            return dispatch(FetchActions.reportFetchError(error));
        }
    };
}

export function deploy(activateTriggers: boolean, deploymentDescription: string): ThunkResult<void, State> {
    return async (dispatch, getState) => {
        dispatch(FetchActions.fetchStart(FetchId.deploy));
        const state = getState();
        const deployDialogState = state.deployDialogState;
        const targetAgentGroup = getAgentGroup(state, deployDialogState.targetAgentGroupName);
        try {
            await postDeploy(deployDialogState.versionsToDeploy, deployDialogState.processVersions,  targetAgentGroup, deployDialogState.isSwitchVersion, activateTriggers, deploymentDescription, state.deployDialogState.suggestedProcessVariablesForDeployment);
            Utilities.toastSuccess(FRENDS.Localizations.Processes_DeployedSuccessfully);
            dispatch(FetchActions.fetchFinish(FetchId.deploy));
            return dispatch(closeDeployDialog());
        } catch (error) {
            dispatch(FetchActions.reportFetchError(error));
        }
    };
}

export function postDeploy(versionsToDeploy: HashMap<number>, processVersions: HashMap<ProcessVersionDetails[]>, agentGroup: AgentGroup | undefined, isSwitchVersion: boolean | undefined, activateTriggers: boolean, deploymentDescription: string, processVariables: HashMap<IProcessVariable[] | undefined>) {
    const processVersionData = Object.keys(versionsToDeploy)
        .map((processGuid) => {
            const processId = processVersions[processGuid].find(v => v.version === versionsToDeploy[processGuid])?.processId;
            if(processId == null) {
                const errorMessage= `Could not find processId for processGuid ${processGuid} and version ${versionsToDeploy[processGuid]}. Please contact support@frends.com.`;
                Utilities.toastError(errorMessage);
                throw new Error(errorMessage);
            }
            return {
                processGuid: processGuid,
                version: versionsToDeploy[processGuid],
                processVariables: processVariables[processId],
            };
        });

    const postData = {
        processVersions: processVersionData,
        isSwitchVersion,
        activateTriggers,
        deploymentDescription: deploymentDescription.trim(),
        processVariables: processVariables,
    };

    return Utilities.postJsonAndParseResponse(`api/processList/deploy/${agentGroup?.environment}/${agentGroup?.name}`, postData);
}

export function openDeployModal(ids: number[], isSwitchVersion: boolean): ThunkResult<void, State> {
    return async (dispatch, getState) => {
        const state = getState();
        const selectedProcesses = state.processes.filter((p) => ids.indexOf(p.id) >= 0);

        // for switch version, only default allowed
        let agentGroupsForDeployment = [state.agentGroups[DEFAULT_AGENT_GROUP_NAME]];

        if (!isSwitchVersion) {
            agentGroupsForDeployment = _.values(state.agentGroups).filter((ag) => ag.name !== state.currentAgentGroupName && ag.name !== DEFAULT_AGENT_GROUP_NAME);
            if (agentGroupsForDeployment.length === 0) {
                Utilities.toastError(FRENDS.Localizations.Deploy_NoEnvironmentsToDeployTo);
                throw new Error("No agent groups to deploy to: cannot deploy to current or default agent group");
            }
        }

        const defaultTargetAgentGroup = agentGroupsForDeployment[0];

        const processVersionDetails = await FetchActions.fetchProcessVersionDetails(selectedProcesses.map((p) => p.uniqueIdentifier));
        const agentVersions = await FetchActions.fetchAgentVersions();

        const suggestedProcessVariablesForDeployment = await FetchActions.fetchSuggestedProcessVariablesForDeployment(defaultTargetAgentGroup.name, selectedProcesses.map(p => p.id));

        return dispatch({
            type: OPEN_DEPLOY_MODAL,
            processes: selectedProcesses,
            processVersions: processVersionDetails,
            isSwitchVersion: isSwitchVersion,
            defaultTargetAgentGroupName: defaultTargetAgentGroup.name,
            defaultTargetEnvironmentName: defaultTargetAgentGroup.environment,
            availableAgentGroups: agentGroupsForDeployment,
            agentVersions: agentVersions,
            suggestedProcessVariablesForDeployment: suggestedProcessVariablesForDeployment,
        });
    }
}